import { useState } from 'react';
import { useAccount } from 'wagmi';
import BigNumber from 'bignumber.js';
import { ExtensionProvider } from '@multiversx/sdk-extension-provider/out';
import { useDispatch } from 'react-redux';
import {
  Address,
  ArgSerializer,
  BigUIntValue,
  BytesValue,
  TokenTransfer,
  TransactionPayload,
  TypedValue,
  Transaction,
} from '@multiversx/sdk-core/out';
import {
  Configuration,
  getNonce,
  getPair,
  sendTransaction,
} from '../helpers/elrondTrading';
import { ICoin } from '../reducers/dex/tradingReducer';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
import { ELROND_CHAIN_ID } from '../../../helpers/customChains';
import { OnRefresh } from '../../../wallet/walletContext';

const gasLimit = 25000000;
const gasPrice = 1000000000;

const getSwapTx = async (
  baseAddress: string,
  baseAmount: BigNumber,
  baseDecimals: number,
  quoteAddress: string,
  quoteAmount: BigNumber,
  quoteDecimals: number,
  userAddress: string,
  prevNonce?: number
) => {
  const amountA = baseAmount.times(10 ** baseDecimals);
  const tokenAPayment = TokenTransfer.fungibleFromBigInteger(
    baseAddress,
    amountA,
    baseDecimals
  );
  const amountB = quoteAmount.times(10 ** quoteDecimals);
  const tokenBPayment = TokenTransfer.fungibleFromBigInteger(
    quoteAddress,
    amountB,
    quoteDecimals
  );
  const pair = await getPair(baseAddress, quoteAddress);
  const nonce =
    prevNonce === undefined ? await getNonce(userAddress) : prevNonce + 1;
  const args: TypedValue[] = [
    // The token A identifier
    BytesValue.fromUTF8(baseAddress),
    // Token A amount
    new BigUIntValue(tokenAPayment.valueOf()),
    // func name
    BytesValue.fromUTF8('swapTokensFixedInput'),
    // The token B identifier
    BytesValue.fromUTF8(quoteAddress),
    // Token B amount
    new BigUIntValue(tokenBPayment.valueOf()),
  ];
  const { argumentsString } = new ArgSerializer().valuesToString(args);
  const data = `ESDTTransfer@${argumentsString}`;

  const swapTx = new Transaction({
    nonce,
    sender: new Address(userAddress),
    receiver: new Address(pair),
    gasPrice,
    gasLimit,
    value: '0',
    data: new TransactionPayload(data),
    chainID: '1',
    version: 1,
  });
  return { prevNonce: nonce, swapTx };
};

const getWrapEgldTx = async (
  base: ICoin,
  quote: ICoin,
  userAddress: string
) => {
  const transactions: Transaction[] = [];

  const amount = base.amount.times(10 ** base.decimals);
  const nonce = await getNonce(userAddress);
  const data = 'wrapEgld';
  const wrapEgldTx = new Transaction({
    nonce,
    sender: new Address(userAddress),
    receiver: new Address(Configuration.wrapEgld),
    gasPrice,
    gasLimit,
    value: amount,
    data: new TransactionPayload(data),
    chainID: '1',
    version: 1,
  });
  transactions.push(wrapEgldTx);
  if (quote.address !== Configuration.egld) {
    const { swapTx } = await getSwapTx(
      Configuration.egld,
      base.amount,
      base.decimals,
      quote.address,
      quote.amount,
      quote.decimals,
      userAddress,
      nonce
    );
    transactions.push(swapTx);
  }
  return transactions;
};

const getUnwrapEgldTx = async (
  base: ICoin,
  quote: ICoin,
  userAddress: string
) => {
  const transactions: Transaction[] = [];
  let nonce = await getNonce(userAddress);

  if (base.address !== Configuration.egld) {
    const { prevNonce, swapTx } = await getSwapTx(
      base.address,
      base.amount,
      base.decimals,
      Configuration.egld,
      quote.amount,
      quote.decimals,
      userAddress
    );
    transactions.push(swapTx);
    nonce = prevNonce + 1;
  }
  const amount = quote.amount.times(10 ** quote.decimals);
  const tokenPayment = TokenTransfer.fungibleFromBigInteger(
    Configuration.egld,
    amount,
    quote.decimals
  );
  const args: TypedValue[] = [
    // WEGLD identifier
    BytesValue.fromUTF8(Configuration.egld),
    // WEGLD amount
    new BigUIntValue(tokenPayment.valueOf()),
    // Convert to EGLD func
    BytesValue.fromUTF8('unwrapEgld'),
  ];
  const { argumentsString } = new ArgSerializer().valuesToString(args);
  const data = `ESDTTransfer@${argumentsString}`;

  const unwrapEgldTx = new Transaction({
    nonce,
    sender: new Address(userAddress),
    receiver: new Address(Configuration.wrapEgld),
    gasPrice,
    gasLimit,
    value: '0',
    data: new TransactionPayload(data),
    chainID: '1',
    version: 1,
  });
  transactions.push(unwrapEgldTx);
  return transactions;
};

enum swapTexts {
  swap = 'Swap',
  allowance = 'Asking allowance',
  allowanceMining = 'Mining allowance',
  txAsking = 'Asking transaction',
  txMining = 'Mining transaction',
}

interface ElrondTradeProps {
  base: ICoin;
  quote: ICoin;
  tokenRoute: string[];
  userAddress?: string;
  onNotify: (input: any) => void;
  onWalletRefresh: OnRefresh;
}

/**
 * Actual trade itself
 * @returns
 */
const useElrondTrade = ({
  base,
  quote,
  tokenRoute,
  userAddress,
  onNotify,
  onWalletRefresh,
}: ElrondTradeProps) => {
  const dispatch = useDispatch();
  const [lock, setLock] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [swapText, setSwapText] = useState<string>(swapTexts.swap);
  const { connector } = useAccount();

  const resetState = () => {
    setLoading(false);
    setLock(false);
    setSwapText(swapTexts.swap);
    onWalletRefresh([base.address, quote.address]);
  };

  /**
   * Actual swap code
   */
  const onSwap = async () => {
    if (!userAddress) {
      return;
    }

    if (!connector || lock) {
      return;
    }

    const wrapOrUnwrapEgldCondition =
      tokenRoute.length === 1 && tokenRoute[0] === Configuration.egld;

    if (tokenRoute.length > 2) {
      onNotify({
        kind: 'warning',
        message: 'Multi pair swap is not supported yet',
      });
      // const gasLimit = 70000000;
      // temporary disabled token selection
      // implement Multi pair swap
      // ESDTTransfer
      // @555344432d633736663166@0f4240 // USDC-1233
      // @6d756c74695061697253776170 // multiPairSwap
      // @00000000000000000500ce7eab736978ce9492ebbf8206f252eacb333cfa5483 // WEGLD/USDC pair address
      // @73776170546f6b656e734669786564496e707574 // swapTokensFixedInput
      // @5745474c442d626434643739 // WEGLD-bd4d79
      // @5214d1e1d37639 // 23103839759726137 (amount of WEGLD)
      // @00000000000000000500969bdcef296da96920851c2a284d0f8db22419bb5483 CRU/USDC pair address
      // @73776170546f6b656e734669786564496e707574 // swapTokensFixedInput
      // @4352552d613566346161 // CRU-a5f4aa
      // @06cb163135f57752 // 489509385114318674 (amount of CRU)
    } else if (tokenRoute.length === 2 || wrapOrUnwrapEgldCondition) {
      setLock(true);

      const provider: ExtensionProvider = await connector.getProvider();

      let transactions: Transaction[];
      if (!base.address) {
        transactions = await getWrapEgldTx(base, quote, userAddress);
      } else if (!quote.address) {
        transactions = await getUnwrapEgldTx(base, quote, userAddress);
      } else {
        const { swapTx } = await getSwapTx(
          base.address,
          base.amount,
          base.decimals,
          quote.address,
          quote.amount,
          quote.decimals,
          userAddress
        );
        transactions = [swapTx];
      }

      try {
        const signedTxs = await provider.signTransactions(transactions);
        for await (const signedTx of signedTxs) {
          const sigTxObject = signedTx.toPlainObject();

          const txResult = await sendTransaction(sigTxObject);

          dispatch(
            actionAppendTransaction(
              toIntermediateTransaction(
                txResult.data.txHash,
                ELROND_CHAIN_ID,
                true
              )
            )
          );

          if (txResult.error.length > 0) {
            onNotify({
              kind: 'warning',
              message: `Something went wrong: ${txResult.error}`,
            });
            return;
          } else {
            onNotify({ message: 'Transaction sent successfully' });
          }
        }
      } catch (e: any) {
        const message = e?.message?.includes('Transaction canceled')
          ? 'User canceled transaction'
          : `Something went wrong: ${e.message}`;
        onNotify({
          kind: 'warning',
          message,
        });
        console.log({ e });
      }

      resetState();
    } else {
      // try to re-estimate
    }
  };

  return {
    loading,
    onSwap,
    swapText,
  };
};

export default useElrondTrade;
